import {
  IonReorderGroup,
  ItemReorderEventDetail,
  useIonPopover,
} from '@ionic/react';
import { cloneDeep, debounce, isNil } from 'lodash';
import {
  Fragment,
  MutableRefObject,
  ReactElement,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import Collapsible from 'react-collapsible';
import { Machine } from '../../../../../api/graphql/generated';
import { WithConfirmation } from '../../../../../components/button/with-confirmation';
import { SaveStatusToast } from '../../../../../components/toast/toast.gql';
import i18n from '../../../../../i18n';
import { getLocalizedText } from '../../../../../i18n/i18n.utils';
import { logger } from '../../../../../util/logger';
import { useFlowServices } from '../../hook/flow-service.hooks';
import { ExecutionInfo } from '../../hook/live-execution.hooks';
import {
  Flow,
  FlowItem,
  FlowItemArrayType,
  FlowItemSectionParent,
  FlowItemText,
  FlowSection,
  FlowState,
  FlowValidateError,
  SystemUnit,
} from '../../model/flow.types';
import {
  convertSectionsToFlowItems,
  createSectionsFromFlowItems,
  getChildSectionsForParent,
  instanceOfFlowItem,
  instanceOfFlowItemSection,
  instanceOfFlowItemSectionParent,
  instanceOfHelper,
  instanceOfSectionEnd,
} from '../../util/flow-item.util';
import { useHasApplicationFieldFieldPermission } from '../../util/permission';
import { FlowItemAction } from '../flow-item/view/flow-item-base';
import {
  FlowItemQuickAction,
  FlowItemQuickActionPopover,
} from './flow-item-action-popover';
import { FlowUpdateMethod, SaveFlowFn, useFlowRefContext } from './flow-ref';
import {
  FlowSectionComponent,
  FlowSectionComponentProps,
} from './flow-section';
import {
  getReorderFromItemParent,
  getReorderToItemParent,
} from './flow-sections.utils';

export interface FlowSectionsProps {
  machine: Machine;
  sections: FlowSection[];
  flowItemTextArr: (FlowItemText | undefined)[];
  flowItemClasses?: string[];
  flow: Flow;
  canAddReorderItem: boolean;
  flowValidationErrors?: FlowValidateError[];
  getActions: (index: number) => FlowItemAction[] | undefined;
  flowState: FlowState;
  selectedItemIds: string[];
  shouldShowExecutionStatus: boolean;
  executionInfo?: ExecutionInfo;
  startItemIndex: number;
  getFlowItemsRef: MutableRefObject<() => FlowItem[]>;
  saveFlow: SaveFlowFn;
  updateFlowStartAt: (
    id: string,
    longPress: boolean,
    withStepMode?: boolean
  ) => Promise<void>;
  system: SystemUnit;
  onAddItemsAfter: (index?: number) => Promise<void>;
  onAddSectionAtIndex: (index: number) => Promise<void>;
  onReorderItems?: (params: {
    fromIndex: number;
    toIndex: number;
    items: FlowItemArrayType[];
  }) => void;
  onSelectItem: (id: string) => string[];
}

export interface FlowSectionsRef {
  toggleCollapseAll: (collapsed: boolean) => void;
}

const FlowSections = forwardRef<FlowSectionsRef, FlowSectionsProps>(
  (props: FlowSectionsProps, ref) => {
    const {
      machine,
      sections,
      flowItemTextArr,
      flow,
      canAddReorderItem,
      flowValidationErrors,
      getActions,
      flowState,
      selectedItemIds,
      shouldShowExecutionStatus,
      executionInfo,
      startItemIndex,
      getFlowItemsRef,
      saveFlow,
      updateFlowStartAt,
      flowItemClasses,
      system,
      onAddItemsAfter,
      onAddSectionAtIndex,
      onReorderItems,
      onSelectItem,
    } = props;

    const { appSlug, flowAnalyticService } = useFlowServices();
    const { flowItems, componentRegistry } = useFlowRefContext();
    const { quickActions } = componentRegistry;

    const [quickActionItem, setQuickActionItem] = useState<
      FlowItem | undefined
    >(undefined);

    const [sectionCollapsedState, setSectionCollapsedState] = useState<
      Record<string, boolean>
    >({});

    const [reorderError, setReorderError] = useState<string | undefined>();

    const getSectionCollapsedState = useRef<() => Record<string, boolean>>(
      () => {
        return sectionCollapsedState;
      }
    );

    useEffect(() => {
      getSectionCollapsedState.current = () => sectionCollapsedState;
    }, [sectionCollapsedState]);

    const canSetStartAt = useHasApplicationFieldFieldPermission(
      appSlug,
      machine?.id,
      'startAt'
    );

    const [presentLongPressAction, dismissLongPressActions] = useIonPopover(
      FlowItemQuickActionPopover,
      {
        onDismiss: (action: FlowItemAction) => {
          dismissLongPressActions(action);
        },
        selectedItem: quickActionItem,
        quickActions,
      }
    );

    const onLongPress = useCallback(
      async (_e: Event, id: string) => {
        if (canSetStartAt) {
          updateFlowStartAt(id, true);
        }
      },
      [canSetStartAt, updateFlowStartAt]
    );

    const presentItemQuickActions = useCallback(
      (e: Event, id: string) => {
        const item = flowItems
          .filter(instanceOfFlowItem)
          .find((i) => i.id === id);

        if (!item) {
          logger.error(
            'Could not present item quick actions. Item not found.',
            {
              id,
            }
          );
        }

        setQuickActionItem(item);

        presentLongPressAction({
          event: e,
          onDidDismiss: (e: CustomEvent) => {
            const action = e.detail.data;

            if (isNil(action)) {
              return;
            }

            if (action === FlowItemQuickAction.INSERT_SECTION) {
              const itemIndex = flow.items.findIndex((i) => i.id === id);
              flowAnalyticService.trackSectionAdd();
              onAddSectionAtIndex(itemIndex);
            } else if (
              !isNil(quickActions) &&
              !isNil(quickActions[action]) &&
              !isNil(item)
            ) {
              quickActions[action].handler(item);
            } else {
              logger.error('Could not handle quick action.', {
                action,
                quickActions: Object.keys(quickActions ?? {}),
                item,
              });
            }
          },
        });
      },
      [
        flowItems,
        presentLongPressAction,
        quickActions,
        flow.items,
        flowAnalyticService,
        onAddSectionAtIndex,
      ]
    );

    const doReorder = useCallback(
      async (
        event: CustomEvent<ItemReorderEventDetail>
      ): Promise<{
        success: boolean;
        items: FlowItemArrayType[];
        message?: string;
      }> => {
        const { from, to } = event.detail;
        const items = cloneDeep(getFlowItemsRef.current());
        const sections = createSectionsFromFlowItems(items);
        const sectionCollapsedState = getSectionCollapsedState.current();

        let actualItems: FlowItemArrayType[] = [];

        const hasCollapsedSection =
          Object.values(sectionCollapsedState).find((state) => state) !==
          undefined;

        // if we have a collapsed section then we need to make sure our array structure mimicks
        // the dom structure for how sections and items are rendered so that when we complete
        // the event the items will be moved to the correct indexes
        if (hasCollapsedSection) {
          let collapsedParentSectionId: string | undefined = undefined;
          for (const section of sections) {
            let shouldAddItems = true;

            // if we have a collapsed section, we need to hide the child sections
            if (!isNil(collapsedParentSectionId)) {
              // if we detect the end of the section, then we can start adding items again
              const isSectionEnd = section.items.find(
                (item) =>
                  instanceOfSectionEnd(item.item) &&
                  item.item.sectionId === collapsedParentSectionId
              );

              if (!isSectionEnd) {
                continue;
              }

              collapsedParentSectionId = undefined;
            }

            if (section.section) {
              const item = section.section?.item;

              if (item) {
                actualItems.push(item);

                // if it's a collapsed parent section, then we need to hide the children
                if (
                  instanceOfFlowItemSectionParent(item) &&
                  sectionCollapsedState[item.id] === true
                ) {
                  collapsedParentSectionId = item.id;
                  shouldAddItems = false;

                  // if it's a collapsed section, we won't add items
                } else if (item.id && sectionCollapsedState[item.id] === true) {
                  shouldAddItems = false;
                }
              }
            }

            if (shouldAddItems && section.items.length > 0) {
              actualItems.push(
                ...section.items
                  .map((s) => s.item)
                  .filter((i) => i !== undefined)
              );
            }
          }
        } else {
          actualItems = convertSectionsToFlowItems(sections);
        }

        const fromItem = actualItems[from];
        const fromItemIsParent = instanceOfFlowItemSectionParent(fromItem);

        if (onReorderItems) {
          onReorderItems({ fromIndex: from, toIndex: to, items: actualItems });
        }

        const validStatus = validateReorder({
          from,
          to,
          actualItems,
        });

        if (!validStatus.valid) {
          await event.detail.complete(false);
          return {
            success: false,
            items: items,
            message:
              validStatus.message ??
              getLocalizedText('common:reorder_flow_items_error_message'),
          };
        }

        const newItems: FlowItemArrayType[] = await event.detail.complete(
          cloneDeep(actualItems)
        );

        // if the section being moved is a parent, we need to move any
        // uncollapsed children
        if (fromItemIsParent) {
          const firstItemId = (actualItems[from + 1] as FlowItem).id;
          const childrenStartIndex = newItems.findIndex((i) => {
            if (instanceOfHelper(i)) {
              return instanceOfSectionEnd(i) && i.sectionId === fromItem.id;
            } else {
              return i.id === firstItemId;
            }
          });
          const childrenEndIndex = newItems.findIndex(
            (i) => instanceOfSectionEnd(i) && i.sectionId === fromItem.id
          );

          // pull the children out of the array
          const childrenItems = newItems.splice(
            childrenStartIndex,
            childrenEndIndex - childrenStartIndex + 1
          );

          const newStartIndex = newItems.findIndex(
            (i) => !instanceOfHelper(i) && i.id === fromItem.id
          );
          // add the children to the new location
          newItems.splice(newStartIndex + 1, 0, ...childrenItems);
        }

        // we need to save all items for collapsed sections
        if (hasCollapsedSection) {
          // if there are any collapsed parent sections, we need to add the
          // section children
          cloneDeep(newItems).forEach((item) => {
            if (
              instanceOfFlowItemSectionParent(item) &&
              sectionCollapsedState[item.id] === true
            ) {
              const childItems = getChildSectionsForParent(
                item.id,
                sections
              ).flatMap((child) => {
                if (child.section?.item) {
                  // if the child is a section and it's not collapsed, then we
                  // need to move the section and the items
                  if (
                    child.section.item.id &&
                    sectionCollapsedState[child.section.item.id] !== true
                  ) {
                    return [
                      child.section.item,
                      ...child.items.map((s) => s.item),
                    ];
                  }
                  // if the child is a section, then we just move the section
                  // since the next loop will handle the section items
                  return child.section.item;
                } else {
                  // if the child doesn't have a section, we move the items
                  return child.items.map((s) => s.item);
                }
              });

              const startIndex = newItems.findIndex(
                (i) => !instanceOfHelper(i) && i.id === item.id
              );

              newItems.splice(startIndex + 1, 0, ...childItems);
            }
          });

          // if there are any collapsed sections, we need to add the section items
          for (const section of sections) {
            if (section.section) {
              const item = section.section?.item;

              if (item.id && sectionCollapsedState[item.id] === true) {
                if (instanceOfFlowItemSectionParent(item)) {
                  continue;
                }

                // find the new location for the section
                const sectionIndex = newItems.findIndex(
                  (i) => i !== undefined && 'id' in i && i.id === item.id
                );

                // if the section contains items, then relocate them here
                if (section.items.length > 0) {
                  newItems.splice(
                    sectionIndex + 1,
                    0,
                    ...section.items.map((s) => s.item)
                  );
                }
              }
            }
          }
        }

        recalculateSectionEnds(newItems);

        const itemsToSave = newItems.filter((i) => !instanceOfHelper(i));

        flowAnalyticService.trackReorderFlowItem();

        // We want to update the UI optimistically so that if there is a flow validation error
        // the user has the opportunity to fix their changes.  It also makes for a more fluid UX
        // because we optimistically update the UI before waiting for the server to come back
        // with the reordered items.
        await saveFlow(itemsToSave, {
          updateMethod: FlowUpdateMethod.LOCAL_ONLY,
        });

        return { success: true, items: itemsToSave };
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [saveFlow, flowAnalyticService]
    );

    const toggleCollapsed = debounce(
      (itemId: string, collapsed: boolean) => {
        if (collapsed) {
          flowAnalyticService.trackSectionCollapse();
        } else {
          flowAnalyticService.trackSectionExpand();
        }

        setSectionCollapsedState({
          ...sectionCollapsedState,
          [itemId]: collapsed,
        });
      },
      500,
      { leading: true, trailing: false }
    );

    const toggleCollapseAll = (collapsed: boolean) => {
      const mappedSections = sections.reduce(
        (m, section) => {
          const sectionId = section.section?.item.id;
          if (sectionId !== undefined) {
            m[sectionId] = collapsed;
          }

          return m;
        },
        {} as Record<string, boolean>
      );

      setSectionCollapsedState({
        ...sectionCollapsedState,
        ...mappedSections,
      });
    };

    useImperativeHandle(ref, () => {
      return { toggleCollapseAll };
    });

    return (
      <>
        <WithConfirmation
          header={i18n.t('common:reorder_flow_items_confirmation')}
        >
          {({ onConfirmed }) => {
            return (
              <IonReorderGroup
                disabled={!canAddReorderItem}
                onIonItemReorder={async (
                  e: CustomEvent<ItemReorderEventDetail>
                ) => {
                  const previousItems = cloneDeep(getFlowItemsRef.current());
                  const { success, items, message } = await doReorder(e);

                  if (!success) {
                    if (message) {
                      setReorderError(message);
                    } else {
                      setReorderError(
                        i18n.t('common:reorder_flow_items_error_message')
                      );
                    }
                    saveFlow(previousItems, {
                      updateMethod: FlowUpdateMethod.LOCAL_ONLY,
                    });
                    return;
                  }

                  onConfirmed({
                    onConfirmation: () => {
                      saveFlow(items, {
                        updateMethod:
                          FlowUpdateMethod.SERVER_WITH_OPTIMISTIC_UPDATE,
                        saveAsDelta: true,
                      });
                    },
                    onCancel: () => {
                      saveFlow(previousItems, {
                        updateMethod: FlowUpdateMethod.LOCAL_ONLY,
                      });
                    },
                  });
                }}
              >
                <FlowNestedSections
                  sections={sections}
                  flowSectionProps={{
                    canAddReorderItem: canAddReorderItem,
                    flowItemTextArr: flowItemTextArr,
                    flowItemClasses: flowItemClasses,
                    flowState: flowState,
                    getActions: getActions,
                    getFlowItemsRef: getFlowItemsRef,
                    onAddItemsAfter: onAddItemsAfter,
                    onLongPress: onLongPress,
                    onSelectItem: onSelectItem,
                    saveFlow: saveFlow,
                    sectionCollapsedState: sectionCollapsedState,
                    selectedItemIds: selectedItemIds,
                    shouldShowExecutionStatus: shouldShowExecutionStatus,
                    startItemIndex: startItemIndex,
                    toggleCollapsed: toggleCollapsed,
                    updateFlowStartAt: updateFlowStartAt,
                    executionInfo: executionInfo,
                    flowValidationErrors: flowValidationErrors,
                    presentItemQuickActions: presentItemQuickActions,
                    system: system,
                  }}
                />
              </IonReorderGroup>
            );
          }}
        </WithConfirmation>
        <SaveStatusToast
          error={reorderError}
          showOnly="error"
          onDidDismiss={() => setReorderError(undefined)}
        />
      </>
    );
  }
);

FlowSections.displayName = 'FlowSections';

type NestedSection = { main: FlowSection; children: FlowSection[] };

interface FlowNestedSectionsProps {
  sections: FlowSection[];
  flowSectionProps: Omit<FlowSectionComponentProps, 'section'>;
}

const FlowNestedSections = (props: FlowNestedSectionsProps): ReactElement => {
  const { sections, flowSectionProps } = props;
  const { sectionCollapsedState, canAddReorderItem } = flowSectionProps;

  const groupedSections = sections.reduce((acc, section) => {
    if (
      section.section?.parentItem === undefined ||
      section.section?.parentItem === null
    ) {
      acc.push({ main: section, children: [] });
    } else {
      const parentId = section.section.parentItem.id;

      let parent: NestedSection | undefined = undefined;
      if (parentId) {
        parent = acc.find((s) => s.main.section?.item.id === parentId);
      } else {
        // an undefined parentId means that this is a copied section...
        // this means a tempId should exist and the child should have a
        // reference to the parent section (parentSectionTempId)
        parent = acc.find(
          (s) =>
            s.main.section?.item.attributes?.tempId !== undefined &&
            s.main.section.item.attributes.tempId ===
              section.section?.item.attributes?.parentSectionTempId
        );
      }

      if (parent) {
        parent.children.push(section);
      }
    }

    return acc;
  }, [] as NestedSection[]);

  return (
    <>
      {groupedSections.map((section, sectionIndex) => {
        const isCollapsed = section.main.section?.item.id
          ? sectionCollapsedState[section.main.section.item.id]
          : false;

        return (
          <Fragment key={sectionIndex}>
            <FlowSectionComponent
              key={sectionIndex}
              {...flowSectionProps}
              section={section.main}
            />
            {canAddReorderItem ? (
              <>
                {!isCollapsed &&
                  section.children.map((child, childIndex) => {
                    return (
                      <FlowSectionComponent
                        key={childIndex}
                        {...flowSectionProps}
                        section={child}
                      />
                    );
                  })}
              </>
            ) : (
              <Collapsible
                trigger={<></>}
                open={
                  !sectionCollapsedState[section.main.section?.item.id ?? '']
                }
                easing={'ease-in-out'}
              >
                {section.children.map((child, childIndex) => {
                  return (
                    <FlowSectionComponent
                      key={childIndex}
                      {...flowSectionProps}
                      section={child}
                    />
                  );
                })}
              </Collapsible>
            )}
          </Fragment>
        );
      })}
    </>
  );
};

const recalculateSectionEnds = (items: FlowItemArrayType[]) => {
  let section: FlowItemSectionParent | undefined = undefined;
  let lastItemOfSection: FlowItem | undefined = undefined;
  for (const item of items) {
    if (instanceOfFlowItemSectionParent(item)) {
      section = item;
    } else if (!instanceOfHelper(item)) {
      lastItemOfSection = item;
    } else if (instanceOfSectionEnd(item) && section) {
      section.attributes.endItemId = lastItemOfSection?.id;
    }
  }
};

const validateReorder = (params: {
  from: number;
  to: number;
  actualItems: FlowItemArrayType[];
}): { valid: boolean; message?: string } => {
  const { from, to, actualItems } = params;

  const fromItem = actualItems[from];
  const toItemParent = getReorderToItemParent({ from, to, actualItems });
  const fromItemParent = getReorderFromItemParent({ from, to, actualItems });

  // a section parent may not be moved into a section parent
  if (instanceOfFlowItemSectionParent(fromItem) && !isNil(toItemParent)) {
    return {
      valid: false,
      message: getLocalizedText(
        'common:reorder_flow_items_nested_parent_error_message'
      ),
    };
  }

  // a subsection may only be moved into a section parent
  if (
    instanceOfFlowItemSection(fromItem) &&
    !instanceOfFlowItemSectionParent(fromItem) &&
    fromItemParent !== toItemParent
  ) {
    return {
      valid: false,
      message: getLocalizedText(
        'common:reorder_flow_items_nested_segment_error_message'
      ),
    };
  }

  return { valid: true };
};

export { FlowSections };
